/*
 * FEATURE 071 — `'admin'` SAIU DA UNIÃO, e não por arrumação.
 *
 * A área interna não tem senha. Enquanto `'admin'` existia aqui, existia também um caminho que
 * abria um campo `type="password"` sobre a área interna e o postava em
 * `/admin/v1/auth/reauthenticate` — uma rota apagada. Ele era inalcançável por CONSEQUÊNCIA
 * (nenhuma sessão de admin é persistente, então nada devolvia 428), nunca por decisão: bastava uma
 * rota voltar a responder 428 para o formulário reaparecer.
 *
 * Foi um juiz independente que achou, e vale registrar por quê: a varredura estrutural procurava
 * campo de senha em `AdminLoginPage.tsx`, e este vivia num componente COMPARTILHADO, fora do
 * recorte. Ausência que depende de ninguém chamar não é ausência.
 */
export type RecentAuthenticationSurface = 'staff';

type Retry = () => Promise<Response>;
type Challenge = (surface: RecentAuthenticationSurface, retry: Retry) => Promise<Response | null>;

let challenge: Challenge | null = null;
let activeChallenge: {
  surface: RecentAuthenticationSurface;
  result: Promise<Response | null>;
} | null = null;

/**
 * A memory-only bridge between typed API clients and the single accessible password dialog.
 * It intentionally stores neither the password nor a request outside the active document.
 */
export function setRecentAuthenticationChallenge(next: Challenge | null): () => void {
  challenge = next;
  return () => {
    if (challenge === next) challenge = null;
  };
}

export function retryAfterRecentAuthentication(
  surface: RecentAuthenticationSurface,
  retry: Retry,
): Promise<Response | null> {
  if (!challenge) return Promise.resolve(null);

  if (activeChallenge) {
    if (activeChallenge.surface === surface) {
      // One password confirmation opens the server-side window for every concurrent operation on
      // this surface. The first request is retried by the dialog; each joiner retries itself once.
      return activeChallenge.result.then((response) => (response ? retry() : null));
    }

    // Staff and admin are distinct identities. Let the active surface settle, then open the other
    // challenge rather than stacking two dialogs/focus traps.
    return activeChallenge.result.then(() => retryAfterRecentAuthentication(surface, retry));
  }

  const challengeHandler = challenge;
  const result = challengeHandler(surface, retry).finally(() => {
    if (activeChallenge?.result === result) activeChallenge = null;
  });
  activeChallenge = { surface, result };
  return result;
}
